import React, { useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import * as XLSX from "xlsx";
import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "70%",
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 3,
};

const ClientExcelUploadModal = () => {
  const [open, setOpen] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setHeaders([]);
    setInvalidRows([]);
    setValidRows([]);
  };

  // 📌 Read Excel File
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const binaryStr = evt.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });

        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Get headers dynamically
        const headerRow = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
        })[0] || [];

        setHeaders(headerRow);

        // Get full data dynamically
        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          defval: "",
          raw: false,
          blankrows: false,
        });

        validateRows(jsonData, headerRow);

      } catch (error) {
        console.error(error);
        toast.error("Invalid Excel file");
      }
    };

    reader.readAsBinaryString(file);
  };

  // 📌 Validate Rows (first column required)
  const validateRows = (data, headerRow) => {
    if (!headerRow.length) {
      toast.error("Excel file has no headers");
      return;
    }

    const primaryKey = headerRow[0];

    const accepted = [];
    const rejected = [];

    data.forEach((row, index) => {
      const value = row?.[primaryKey];

      if (!value || value.toString().trim() === "") {
        rejected.push({
          rowNumber: index + 2,
          ...row,
        });
      } else {
        accepted.push(row);
      }
    });

    setValidRows(accepted);
    setInvalidRows(rejected);
  };

  // 📌 Continue Upload
  const handleContinue = async () => {
    try {
      if (!validRows.length) {
        toast.error("No valid rows to insert");
        return;
      }

      setLoading(true);

      // Step 1: Dynamic address extraction
      const addressPayload = [];

      validRows.forEach((row) => {
        if (row.address)
          addressPayload.push({ address: row.address });

        if (row.shippingAddress)
          addressPayload.push({ address: row.shippingAddress });
      });

      let generatedIds = [];

      // Step 2: Insert addresses if exist
      if (addressPayload.length) {
        const addressRes = await axiosWithToken.post(
          BASE_URL + "addresses/bulk",
          addressPayload
        );

        const startId = addressRes.data.insertId;

        generatedIds = addressPayload.map(
          (_, index) => startId + index
        );
      }

      // Step 3: Map dynamic client data
      let addressIndex = 0;

      const finalClients = validRows.map((row) => {

        const client = {};

        headers.forEach((header) => {
          client[header] = row[header] ?? "";
        });

        // Replace address with IDs if exist
        if (client.address) {
          client.address = generatedIds[addressIndex++] ?? null;
        }

        if (client.shippingAddress) {
          client.shippingAddress = generatedIds[addressIndex++] ?? null;
        }

        return client;
      });

      // Step 4: Insert clients dynamically
      await axiosWithToken.post(
        BASE_URL + "clients/bulk",
        finalClients
      );

      toast.success(
        `Successfully imported ${finalClients.length} clients`
      );

      handleClose();

      setTimeout(() => {
        window.location.reload();
      }, 1200);

    } catch (error) {
      console.error(error);
      toast.error("Import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={handleOpen}
      >
        Upload Excel
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>

          <Typography variant="h6" textAlign="center" mb={2}>
            Upload Client Excel
          </Typography>

          <Typography textAlign="right" mb={2}>
            Rejected: {invalidRows.length} | Accepted: {validRows.length}
          </Typography>

          <Button
            variant="contained"
            component="label"
            sx={{ mb: 2 }}
          >
            Select Excel File
            <input
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
            />
          </Button>

          {/* Invalid Rows Table */}
          {invalidRows.length > 0 && (
            <>
              <Typography fontWeight="bold" mb={1}>
                Rejected Rows
              </Typography>

              <TableContainer component={Paper}>
                <Table size="small">

                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      {headers.map((header, index) => (
                        <TableCell key={index}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {invalidRows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>

                        <TableCell>
                          {row.rowNumber}
                        </TableCell>

                        {headers.map((header, colIndex) => (
                          <TableCell key={colIndex}>
                            {row[header]}
                          </TableCell>
                        ))}

                      </TableRow>
                    ))}
                  </TableBody>

                </Table>
              </TableContainer>
            </>
          )}

          <Box
            mt={3}
            display="flex"
            justifyContent="space-between"
          >
            <Button
              variant="contained"
              color="error"
              onClick={handleClose}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              disabled={!validRows.length || loading}
              onClick={handleContinue}
            >
              {loading ? "Uploading..." : "Continue"}
            </Button>

          </Box>

        </Box>
      </Modal>
    </>
  );
};

export default ClientExcelUploadModal;
